import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Modal,
  ModalFooter,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import BarcodeGenerationModal from './BarcodeGenerationModal';
import SequentialBarcodeSection from '../components/SequentialBarcodeSection';

// ✅ NEW: Duplicate Items Modal for creating multiple copies with barcode sequence
const DuplicateItemsModal = ({
  open,
  onClose,
  onDuplicateItems,
  originalItem,
  holdingLocationId,
  template,
  classificationNumber,
  authorNumber,
  currentItemsCount,
}) => {
  const intl = useIntl();
  const [itemCount, setItemCount] = useState(1);
  const [showAdvancedBarcodeModal, setShowAdvancedBarcodeModal] =
    useState(false);
  const [barcodeSettings, setBarcodeSettings] = useState(null);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setItemCount(1);
      setBarcodeSettings(null);
    }
  }, [open]);

  // Handle barcode settings change from the reusable component
  const handleBarcodeSettingsChange = (settings) => {
    setBarcodeSettings(settings);
  };

  const handleDuplicate = () => {
    const validItemCount = Math.max(1, parseInt(itemCount, 10) || 1);
    if (validItemCount > 0) {
      onDuplicateItems(validItemCount, barcodeSettings);
      onClose();
    }
  };

  const handleAdvancedBarcodeConfirm = (settings) => {
    setShowAdvancedBarcodeModal(false);
    onDuplicateItems(Math.max(1, parseInt(itemCount, 10) || 1), settings);
    onClose();
  };

  const isValidItemCount =
    itemCount &&
    !Number.isNaN(parseInt(itemCount, 10)) &&
    parseInt(itemCount, 10) > 0 &&
    parseInt(itemCount, 10) <= 1000;

  // Enhanced form validation
  const isFormValid = () => {
    const basicValidation = isValidItemCount;

    // If barcode settings exist, check if they are valid
    if (barcodeSettings) {
      return basicValidation && barcodeSettings.isValid;
    }

    return basicValidation;
  };

  return (
    <>
      <Modal
        open={open}
        label={
          <FormattedMessage
            id="ui-plugin-inventory-templates.duplicateItemsModal.label"
            defaultMessage="Duplicate Items"
          />
        }
        onClose={onClose}
        size="small"
      >
        <div style={{ padding: '20px' }}>
          <p style={{ marginBottom: '16px' }}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.duplicateItemsModal.description"
              defaultMessage="Create multiple copies of this item:"
            />
          </p>

          {/* Original Item Info */}
          <div
            style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #dee2e6',
            }}
          >
            <div
              style={{
                fontWeight: '600',
                marginBottom: '8px',
                color: '#495057',
              }}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.duplicateItemsModal.originalItem.title"
                defaultMessage="Original Item:"
              />
            </div>
            <div
              style={{
                fontSize: '14px',
                color: '#6c757d',
                fontFamily: 'monospace',
              }}
            >
              <div>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.duplicateItemsModal.originalItem.barcode"
                  defaultMessage="Barcode:"
                />{' '}
                {originalItem?.barcode || (
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.duplicateItemsModal.originalItem.noBarcode"
                    defaultMessage="No barcode"
                  />
                )}
              </div>
              <div>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.duplicateItemsModal.originalItem.copy"
                  defaultMessage="Copy:"
                />{' '}
                {originalItem?.copyNumber || (
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.duplicateItemsModal.originalItem.noCopy"
                    defaultMessage="N/A"
                  />
                )}
              </div>
              {originalItem?.volume ? (
                <div>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.duplicateItemsModal.originalItem.volume"
                    defaultMessage="Volume:"
                  />{' '}
                  {originalItem.volume}
                </div>
              ) : (
                <div>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.duplicateItemsModal.originalItem.volume"
                    defaultMessage="Volume:"
                  />{' '}
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.duplicateItemsModal.originalItem.noVolume"
                    defaultMessage="No volume"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Item Count Input */}
          <div style={{ marginBottom: '16px' }}>
            <TextField
              label={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.duplicateItemsModal.itemCount.label"
                  defaultMessage="Number of Copies to Create"
                />
              }
              type="number"
              min="1"
              max="1000"
              value={itemCount}
              onChange={(e) => setItemCount(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.duplicateItemsModal.itemCount.placeholder',
                defaultMessage: 'Enter number of copies',
              })}
              required
            />
          </div>

          {/* Sequential Barcode Section - Using Reusable Component */}
          {isValidItemCount && (
            <SequentialBarcodeSection
              itemCount={itemCount}
              onBarcodeSettingsChange={handleBarcodeSettingsChange}
              sectionTitle={intl.formatMessage(
                {
                  id: 'ui-plugin-inventory-templates.sequentialBarcode.section.title',
                },
                { count: Math.max(1, parseInt(itemCount, 10) || 1) },
              )}
              checkboxLabel={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.sequentialBarcode.useSequential.label',
              })}
              placeholder={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.sequentialBarcode.startingBarcode.placeholder',
              })}
              onAdvancedClick={() => setShowAdvancedBarcodeModal(true)}
            />
          )}

          {/* Preview */}
          {isValidItemCount && (
            <div
              style={{
                backgroundColor: '#e8f5e8',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '16px',
                border: '1px solid #c3e6c3',
              }}
            >
              <div
                style={{
                  fontWeight: '600',
                  marginBottom: '4px',
                  color: '#2d5a2d',
                }}
              >
                <FormattedMessage
                  id="ui-plugin-inventory-templates.duplicateItemsModal.preview.label"
                  defaultMessage="Copies to be created:"
                />
              </div>
              <div style={{ fontSize: '14px', color: '#2d5a2d' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.duplicateItemsModal.preview.details"
                  values={{
                    itemCount: Math.max(1, parseInt(itemCount, 10) || 1),
                    startCopyNumber: currentItemsCount + 1,
                    endCopyNumber:
                      currentItemsCount +
                      Math.max(1, parseInt(itemCount, 10) || 1),
                  }}
                  defaultMessage="{itemCount} copies with copy numbers {startCopyNumber} to {endCopyNumber}"
                />
              </div>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button
            buttonStyle="primary"
            onClick={handleDuplicate}
            disabled={!isFormValid()}
            marginBottom0
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.duplicateItemsModal.create"
              values={{ count: parseInt(itemCount, 10) || 0 }}
              defaultMessage="Create {count} Copies"
            />
          </Button>
          <Button onClick={onClose} marginBottom0>
            <FormattedMessage
              id="ui-plugin-inventory-templates.modal.cancel"
              defaultMessage="Cancel"
            />
          </Button>
        </ModalFooter>
      </Modal>

      {/* Advanced Barcode Generation Modal */}
      <BarcodeGenerationModal
        open={showAdvancedBarcodeModal}
        onClose={() => setShowAdvancedBarcodeModal(false)}
        onConfirm={handleAdvancedBarcodeConfirm}
        itemCount={Math.max(1, parseInt(itemCount, 10) || 1)}
        enableAutoGenerator={false}
        autoGenerate={false}
        generateBarcodeValue={() => ''}
        locationId={holdingLocationId}
        locationCode=""
        existingBarcodes={[]}
        title={
          <FormattedMessage
            id="ui-plugin-inventory-templates.duplicateItemsModal.advancedBarcodeModal.title"
            defaultMessage="Advanced Barcode Generation Settings"
          />
        }
      />
    </>
  );
};

DuplicateItemsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDuplicateItems: PropTypes.func.isRequired,
  originalItem: PropTypes.object,
  holdingLocationId: PropTypes.string,
  template: PropTypes.object,
  classificationNumber: PropTypes.string,
  authorNumber: PropTypes.string,
  currentItemsCount: PropTypes.number,
};

export default DuplicateItemsModal;
